import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { mediaInsertMessages } from '@atlaskit/editor-common/messages';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { Browser } from '@atlaskit/media-picker';
import { Stack } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { useAnalyticsEvents } from './useAnalyticsEvents';
var INITIAL_UPLOAD_STATE = Object.freeze({
  isOpen: false,
  error: null
});
var uploadReducer = function uploadReducer(state, action) {
  switch (action.type) {
    case 'open':
      return _objectSpread(_objectSpread({}, INITIAL_UPLOAD_STATE), {}, {
        isOpen: true
      });
    case 'close':
      // This is the only case where we don't reset state. This is because
      // onClose gets called for cancel _and_ upload, so we don't want to
      // reset any loading or error states that may have occured
      return _objectSpread(_objectSpread({}, state), {}, {
        isOpen: false
      });
    case 'error':
      return _objectSpread(_objectSpread({}, INITIAL_UPLOAD_STATE), {}, {
        error: action.error
      });
    case 'reset':
      return INITIAL_UPLOAD_STATE;
  }
};
var isImagePreview = function isImagePreview(preview) {
  return 'dimensions' in preview;
};
export var LocalMedia = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var mediaProvider = _ref.mediaProvider,
    insertMediaSingle = _ref.insertMediaSingle,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent,
    closeMediaInsertPicker = _ref.closeMediaInsertPicker;
  var intl = useIntl();
  var strings = {
    upload: intl.formatMessage(mediaInsertMessages.upload),
    networkError: intl.formatMessage(mediaInsertMessages.localFileNetworkErrorMessage),
    genericError: intl.formatMessage(mediaInsertMessages.localFileErrorMessage)
  };
  var _useAnalyticsEvents = useAnalyticsEvents(dispatchAnalyticsEvent),
    onUploadButtonClickedAnalytics = _useAnalyticsEvents.onUploadButtonClickedAnalytics,
    onUploadCommencedAnalytics = _useAnalyticsEvents.onUploadCommencedAnalytics,
    onUploadSuccessAnalytics = _useAnalyticsEvents.onUploadSuccessAnalytics,
    onUploadFailureAnalytics = _useAnalyticsEvents.onUploadFailureAnalytics;
  var _React$useReducer = React.useReducer(uploadReducer, INITIAL_UPLOAD_STATE),
    _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
    uploadState = _React$useReducer2[0],
    dispatch = _React$useReducer2[1];
  var onUpload = function onUpload(_ref2) {
    var _mediaProvider$upload;
    var file = _ref2.file,
      preview = _ref2.preview;
    onUploadSuccessAnalytics('local');
    var mediaState = {
      id: file.id,
      collection: (_mediaProvider$upload = mediaProvider.uploadParams) === null || _mediaProvider$upload === void 0 ? void 0 : _mediaProvider$upload.collection,
      fileMimeType: file.type,
      fileSize: file.size,
      fileName: file.name,
      dimensions: undefined
    };
    if (isImagePreview(preview)) {
      mediaState.dimensions = {
        width: preview.dimensions.width,
        height: preview.dimensions.height
      };
    }
    insertMediaSingle({
      mediaState: mediaState,
      inputMethod: INPUT_METHOD.MEDIA_PICKER
    });
    closeMediaInsertPicker();

    // Probably not needed but I guess it _could_ fail to close for some reason
    dispatch({
      type: 'reset'
    });
  };
  var uploadParams = mediaProvider.uploadParams,
    uploadMediaClientConfig = mediaProvider.uploadMediaClientConfig;
  return /*#__PURE__*/React.createElement(Stack, {
    grow: "fill",
    space: "space.200"
  }, uploadState.error && /*#__PURE__*/React.createElement(SectionMessage, {
    appearance: "error"
  }, uploadState.error === 'upload_fail' ? strings.networkError : strings.genericError), /*#__PURE__*/React.createElement(Button, {
    iconBefore: UploadIcon,
    ref: ref,
    shouldFitContainer: true,
    isDisabled: !uploadMediaClientConfig || !uploadParams,
    onClick: function onClick() {
      onUploadButtonClickedAnalytics();
      dispatch({
        type: 'open'
      });
    }
  }, strings.upload), uploadMediaClientConfig && uploadParams && /*#__PURE__*/React.createElement(Browser, {
    isOpen: uploadState.isOpen,
    config: {
      uploadParams: uploadParams
    },
    mediaClientConfig: uploadMediaClientConfig,
    onUploadsStart: function onUploadsStart() {
      onUploadCommencedAnalytics('local');
    },
    onPreviewUpdate: onUpload
    // NOTE: this will fire for some errors like network failures, but not
    // for others like empty files. Those have their own feedback toast
    // owned by media.
    ,
    onError: function onError(payload) {
      onUploadFailureAnalytics(payload.error.name, 'local');
      dispatch({
        type: 'error',
        error: payload.error.name
      });
    },
    onClose: function onClose() {
      dispatch({
        type: 'close'
      });
    }
  }));
});